/* Paper catalogue for the Print Center (ported 1:1 from the prototype's PAPERS). */

/** Paper family decides which documents/printers fit. */
export type PaperFam = "page" | "roll" | "card" | "label" | "labelsheet";

export interface Paper {
  key: string;
  name: string;
  w: number;        // mm
  h: number;        // mm (0 = continuous roll)
  fam: PaperFam;
  group: string;
}

const P = (key: string, name: string, w: number, h: number, fam: PaperFam, group: string): Paper => ({ key, name, w, h, fam, group });

export const PAPER_LIST: Paper[] = [
  P("A3", "A3", 297, 420, "page", "ISO A"), P("A4", "A4", 210, 297, "page", "ISO A"), P("A5", "A5", 148, 210, "page", "ISO A"), P("A6", "A6", 105, 148, "page", "ISO A"),
  P("B4", "B4", 250, 353, "page", "ISO B"), P("B5", "B5", 176, 250, "page", "ISO B"),
  P("LTR", "Letter", 215.9, 279.4, "page", "North America"), P("LGL", "Legal", 215.9, 355.6, "page", "North America"), P("TAB", "Tabloid / Ledger", 279.4, 431.8, "page", "North America"), P("EXE", "Executive", 184.2, 266.7, "page", "North America"), P("STM", "Statement (Half Letter)", 139.7, 215.9, "page", "North America"),
  P("DL", "Envelope DL", 110, 220, "page", "Envelopes"), P("C5", "Envelope C5", 162, 229, "page", "Envelopes"), P("C4", "Envelope C4", 229, 324, "page", "Envelopes"),
  P("CHQ", "Cheque 8″ × 3.5″", 203.2, 88.9, "page", "Cheques"),
  P("CF95", "Continuous 9.5″ × 11″", 241.3, 279.4, "page", "Dot-matrix continuous"), P("CF15", "Continuous 15″ × 11″ (wide)", 381, 279.4, "page", "Dot-matrix continuous"), P("CF10", "Continuous 10″ × 12″", 254, 304.8, "page", "Dot-matrix continuous"),
  P("R80", "Thermal roll 80 mm", 80, 0, "roll", "Receipt rolls"), P("R58", "Thermal roll 58 mm", 58, 0, "roll", "Receipt rolls"),
  P("CR80", "ID card CR80 (85.6 × 54 mm)", 85.6, 53.98, "card", "Cards"),
  P("L46", "Label 4″ × 6″", 101.6, 152.4, "label", "Labels"), P("L105", "Label 100 × 50 mm", 100, 50, "label", "Labels"), P("L7050", "Label 70 × 37 mm (A4 sheet of 24)", 210, 297, "labelsheet", "Labels"),
  P("CUSTOM", "Custom size", 0, 0, "page", "Custom"),
];

export const PAPERS: Record<string, Paper> = Object.fromEntries(PAPER_LIST.map((p) => [p.key, p]));

export type MarginKey = "none" | "narrow" | "normal" | "wide";
export const MARGINS: Record<MarginKey, number> = { none: 0, narrow: 6, normal: 12, wide: 20 };
/** CSS px per millimetre. */
export const MM = 3.7795;

export const paperFam = (key: string): PaperFam => (PAPERS[key] ?? PAPERS.A4).fam;

export interface Dims { w: number; h: number; fam: PaperFam; m: number }

/** Effective sheet dimensions (orientation + custom size applied) and the padding (margin). */
export function dims(o: { paper: string; orient: "portrait" | "landscape"; margin: MarginKey; cw: number; ch: number }): Dims {
  const P0 = PAPERS[o.paper] ?? PAPERS.A4;
  let w = P0.w, h = P0.h;
  if (P0.key === "CUSTOM") { w = +o.cw || 210; h = +o.ch || 297; }
  if (o.orient === "landscape" && h) { const x = w; w = h; h = x; }
  const mg = MARGINS[o.margin] ?? 12;
  const m = P0.fam === "page" || P0.fam === "labelsheet" ? mg : P0.fam === "roll" ? 3 : mg > 3 ? 3 : mg;
  return { w, h, fam: P0.fam, m };
}

/** CSS for the @page rule matching the sheet (h = measured height for rolls). */
export const pageCss = (w: number, h: number) => `@page { size: ${w}mm ${h}mm; margin: 0; }`;
